import {
  ConflictResolution,
  type ExportServiceAccountsMutation,
} from '../../../../generated/id-service-env-administration';
import type { IncomingServiceAccount } from './ServiceAccountImportPreview.types';
import { ConflictResolutions } from './ServiceAccountImportPreview.types';

export interface ParsedServiceAccountsExport {
  version: string;
  serviceAccounts: IncomingServiceAccount[];
}

const isPermissionRef = (
  value: unknown,
): value is { serviceId: string; name: string } => {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const ref = value as { serviceId?: unknown; name?: unknown };
  return typeof ref.serviceId === 'string' && typeof ref.name === 'string';
};

const isIncomingServiceAccount = (
  value: unknown,
): value is IncomingServiceAccount => {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const account = value as Partial<IncomingServiceAccount>;
  return (
    typeof account.name === 'string' &&
    typeof account.isProtected === 'boolean' &&
    Array.isArray(account.permissions) &&
    account.permissions.every(isPermissionRef)
  );
};

export const parseServiceAccountsExportFile = async (
  file: File,
): Promise<ParsedServiceAccountsExport | null> => {
  let payload: Partial<
    Pick<
      ExportServiceAccountsMutation['exportServiceAccounts'],
      'version' | 'serviceAccounts'
    >
  >;
  try {
    payload = JSON.parse(await file.text());
  } catch {
    return null;
  }

  if (
    typeof payload?.version !== 'string' ||
    !Array.isArray(payload.serviceAccounts) ||
    !payload.serviceAccounts.every(isIncomingServiceAccount)
  ) {
    return null;
  }

  return {
    version: payload.version,
    serviceAccounts: payload.serviceAccounts,
  };
};

export const toBackendConflictResolution = (
  conflictResolution: ConflictResolutions,
): ConflictResolution => {
  switch (conflictResolution) {
    case ConflictResolutions.OverwriteAll: {
      return ConflictResolution.OverwriteAll;
    }
    case ConflictResolutions.OverwriteNonProtected: {
      return ConflictResolution.OverwriteNonProtected;
    }
    case ConflictResolutions.Skip:
    case ConflictResolutions.NotSelected:
    default: {
      // A safe fallback if the user proceeds without picking a resolution.
      return ConflictResolution.Skip;
    }
  }
};
